import type { CellContext } from '@tanstack/react-table';
import type moment from 'moment';
import * as React from 'react';
import { type WrappedFieldArrayProps } from 'redux-form';

import type ContactVM from 'ab-viewModels/contact/contact';
import type { ContactForInvoicesVM } from 'ab-viewModels/contact/contactsForInvoices.viewModel';

import { formatDate } from '@acceligentllc/shared/utils/time';

import { EmailTypes } from '@acceligentllc/shared/enums/contactMethodType';
import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { UpsertContactStatus } from '@acceligentllc/shared/enums/upsertContactStatus';

import type { SimpleTableRow } from 'af-fields/SimpleTable';
import SimpleTableField from 'af-fields/SimpleTable';

import type { Column, FooterButton } from 'af-components/Controls/SimpleTable/types';
import CreateContactModal from 'af-components/SharedForms/Contacts/SelectContact/CreateContactModal';

import BillingContactField from './BillingContactField';
import styles from './styles.module.scss';
import type { InvoiceFM } from '../CreateOrEdit/formModel';

type ValidationError = {
	[index: number]: { [key: string]: string; };
};

interface BaseContactTableInfo {
	id: number;
	email: string;
	/** ISO_DATETIME */
	lastReminderSent: Nullable<string>;
}

type ContactMethod = BaseContactTableInfo & {
	type: InvoiceBillingLookupType.CONTACT;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	contactMethodId: number;
};

type BillingEmail = {
	type: InvoiceBillingLookupType.EMAIL_ONLY;
	email: string;
};

type EmailOnlyInfo = BaseContactTableInfo & BillingEmail;

type ContactTableInfo = EmailOnlyInfo | ContactMethod;

export type InvoiceBillingContactFM = Required<InvoiceFM>['billingContactMethods'][0];

type CreateContactModalState = {
	showModal: boolean;
	contact: ContactTableInfo | null;
	inputValue: string | null;
	fieldName: string | null;
};

export interface OwnProps {
	change: (fieldName: string, value: Nullable<ContactTableInfo>) => void;
	availableBillingContacts: ContactForInvoicesVM[];
	billingContactMethods: ContactTableInfo[];
	validationErrors: ValidationError;
	emailReminderSent: boolean;
}

type Props = OwnProps & WrappedFieldArrayProps<ContactTableInfo>;

const _isInputPartialEmail = (_inputValue: Nullable<string>) => _inputValue?.includes('@') ?? false;

const BillingContactArray: React.FC<Props> = React.memo((props) => {
	const {
		fields,
		change,
		validationErrors,
		availableBillingContacts = [],
		billingContactMethods,
	} = props;
	const [availableContacts, setAvailableContacts] = React.useState(availableBillingContacts);
	const [createContactModal, setCreateContactModal] = React.useState<CreateContactModalState>({
		showModal: false,
		contact: null,
		inputValue: null,
		fieldName: null,
	});

	React.useEffect(() => {
		setAvailableContacts((current) => {
			const parentIds = new Set(availableBillingContacts.map((c) => c.contactMethodId));
			const localOnly = current.filter((c) => !parentIds.has(c.contactMethodId));

			return [...availableBillingContacts, ...localOnly];
		});
	}, [availableBillingContacts]);

	const initialValues = React.useMemo(() => {
		const inputValue = createContactModal.inputValue;

		if (!inputValue) {
			return {};
		}

		if (_isInputPartialEmail(inputValue)) {
			return {
				emails: [{
					value: inputValue,
					type: EmailTypes.EMAIL_DEFAULT,
					status: UpsertContactStatus.ADDED,
				}],
			};
		}

		return {
			fullName: inputValue,
		};
	}, [createContactModal.inputValue]);

	const openCreateContactModal = React.useCallback((contact: ContactTableInfo, inputValue: string | null, fieldName: string) => {
		setCreateContactModal({
			showModal: true,
			contact,
			inputValue,
			fieldName,
		});
	}, []);

	const closeCreateContactModal = React.useCallback(() => {
		setCreateContactModal({
			showModal: false,
			contact: null,
			inputValue: null,
			fieldName: null,
		});
	}, []);

	const validationForTable = React.useMemo(() => Object.values(validationErrors), [validationErrors]);

	const addItem = React.useCallback(async () => {
		fields.push({
			id: new Date().getTime(), // we need some temporary numeric value that won't repeat if we want to add multiple rows
			lastReminderSent: null,
			type: InvoiceBillingLookupType.CONTACT,
			email: '',
			title: null,
			contactId: new Date().getTime() + new Date().getSeconds(),
			fullName: '',
			companyName: null,
			contactMethodId: new Date().getTime(), // Fake ID
		});
	}, [fields]);

	const addNewFields = React.useCallback((newContacts: ContactMethod[]) => {
		for (const contact of newContacts) {
			fields.push(contact);
		}
	}, [fields]);

	const footerButtonsLeft = React.useMemo(() => {
		const resolvedFooterButtons: FooterButton[] = [{
			iconName: 'ActionPlusSmall',
			label: 'Add Billing Contact',
			onClick: addItem,
		}];

		return resolvedFooterButtons;
	}, [addItem]);

	const fullNameCell = React.useCallback((_cell) => {
		if (!_cell.row.original.isInEditMode) {
			return _cell.getValue() ?? '-';
		} else {
			const fieldName = `${_cell.row.original.name}`;
			let emailValidationError: Nullable<string> = null;

			if (Object.hasOwn(validationErrors, _cell.row.original.index)) {
				emailValidationError = validationErrors[_cell.row.original.index].fullName;
			}

			return (
				<BillingContactField
					availableBillingContacts={availableContacts}
					billingContacts={billingContactMethods}
					change={change}
					currentContact={_cell.row.original as ContactTableInfo}
					emailValidationError={emailValidationError}
					fieldName={fieldName}
					onCreateNew={openCreateContactModal}
				/>
			);
		}
	}, [availableContacts, billingContactMethods, change, openCreateContactModal, validationErrors]);

	const createContactFromModal = React.useCallback((newContact: ContactVM) => {
		const { fieldName } = createContactModal;

		if (!fieldName) {
			return;
		}

		const newInvoiceContacts: ContactMethod[] = [];
		const newContactsForDropdown: ContactForInvoicesVM[] = [];

		newContact.emails.forEach((_email, _index) => {
			const contactMethod: ContactMethod = {
				id: _email.id,
				contactId: newContact.id,
				companyName: newContact.companyName,
				fullName: newContact.fullName,
				email: _email.value,
				title: newContact.title ?? null,
				lastReminderSent: null,
				type: InvoiceBillingLookupType.CONTACT,
				contactMethodId: _email.id,
			};

			newContactsForDropdown.push({
				id: _email.id,
				title: newContact.title ?? null,
				fullName: newContact.fullName,
				companyName: newContact.companyName ?? null,
				email: _email.value,
				contactMethodId: _email.id,
				contactId: newContact.id,
			});

			if (_index === 0) {
				change(fieldName, contactMethod); // now targets the real form path
			} else {
				newInvoiceContacts.push({
					...contactMethod,
					id: new Date().getTime(),
				});
			}
		});

		if (newInvoiceContacts.length > 0) {
			addNewFields(newInvoiceContacts);
		}

		setAvailableContacts((contacts) => [
			...contacts,
			...newContactsForDropdown,
		]);
	}, [addNewFields, change, createContactModal]);

	const columns: Column<ContactTableInfo & SimpleTableRow>[] = React.useMemo(() => {
		return [
		{
			id: 'fullName',
			accessor: 'fullName',
			header: 'Name',
			enableSorting: true,
			size: 400,
			cell: fullNameCell,
		},
		{
			id: 'companyName',
			cell: (_cell) => {
				if (!_cell.row.original.isInEditMode) {
					return _cell.getValue() ?? '-';
				}
			},
			header: 'Company',
			accessor: 'companyName',
			enableSorting: true,
		},
		{
			id: 'title',
			cell: (_cell) => {
				if (!_cell.row.original.isInEditMode) {
					return _cell.getValue() ?? '-';
				}
			},
			size: 200,
			header: 'Title',
			accessor: 'title',
			enableSorting: false,
		},
		{
			id: 'email',
			header: 'Email',
			size: 350,
			cell: (_cell) => {
				if (!_cell.row.original.isInEditMode) {
					return _cell.getValue() ?? '-';
				}
			},
			accessor: 'email',
		},
		{
			id: 'lastReminderSent',
			cell: (_cell: CellContext<ContactTableInfo & SimpleTableRow, moment.MomentInput>) => {
				if (!_cell.row.original.isInEditMode) {
					return formatDate(_cell.getValue(), TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME) ?? '-';
				}
			},
			size: 150,
			header: 'Last Reminder Sent',
			accessor: 'lastReminderSent',
			enableSorting: true,
		},
	]; }, [fullNameCell]);

	return (
		<>
			<div className={`${styles['info-message']}`}>
				Choose a contact from the existing list or create a new one. Alternatively, keep typing an email address (e.g., user.name@email.com) to use.
			</div>

			<SimpleTableField
				allowEdit={true}
				columns={columns}
				emptyTableMessage="No data to show"
				errors={validationForTable}
				fields={fields}
				footerButtonsLeft={footerButtonsLeft}
				initialized={!!fields}
			/>

			<CreateContactModal
				areEmailsRequired={true}
				closeModal={closeCreateContactModal}
				initialValues={initialValues}
				onCreateNew={createContactFromModal}
				showModal={createContactModal.showModal}
			/>
		</>
	);
});

export default BillingContactArray;
